import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _keys from 'lodash/keys';
import { Card } from '@cbhq/cds-web/cards/Card';
import { Grid, HStack } from '@cbhq/cds-web/layout';
import { TextBody, TextLabel2 } from '@cbhq/cds-web/typography';
import { TextTitle1 } from '@cbhq/cds-web/typography/TextTitle1';

import { useGetResources } from ':cloud/queries/PrivilegedAdminQueries/useGetResources';
import { useGetWorkflows } from ':cloud/queries/WorkflowQueries/useGetWorkflows';
import { ICluster } from ':cloud/types/ts_types';
import { filterActiveCluster } from ':cloud/utils/clusters';
import { threeColumnGrid } from ':cloud/utils/responsiveConfigs';
import { Layout } from ':cloud/widgets/layout';
import { LoadingSpinner, ProtocolIcon } from ':cloud/widgets/sharedcomponents';

interface ProtocolStatusCardProps {
  protocolKey: string;
  resources?: ICluster[];
}

function ProtocolStatusCard({ protocolKey, resources }: ProtocolStatusCardProps) {
  const specificProtocolResources = _filter(
    resources,
    (resource: ICluster) => resource?.type === protocolKey,
  );
  const activeResources = _filter(specificProtocolResources, filterActiveCluster);
  const activeCount = activeResources.length;
  const erroredCount = specificProtocolResources.length - activeCount;
  return (
    <Card elevation={2} background height={50}>
      <HStack gap={10}>
        <HStack width={200} gap={2} alignItems="center">
          <ProtocolIcon protocolKey={protocolKey} size={32} />
          <TextBody as="p" color="foregroundMuted">
            {protocolKey?.replace(/qt/g, ' QT')}
          </TextBody>
        </HStack>
        <HStack gap={4}>
          <div>
            <TextLabel2 as="p" color="foregroundMuted">
              Active
            </TextLabel2>
            <TextTitle1 as="p">{activeCount || '0'}</TextTitle1>
          </div>
          <div>
            <TextLabel2 as="p" color="foregroundMuted">
              Errors
            </TextLabel2>
            <TextTitle1 as="p">{erroredCount || '0'}</TextTitle1>
          </div>
        </HStack>
      </HStack>
    </Card>
  );
}

export function AdminProtocols() {
  const { workflows, isLoading: isWorkflowsLoading } = useGetWorkflows();
  const { resources, isLoading: isResourcesLoading } = useGetResources();
  const filteredResources = _filter(
    resources,
    (cluster: ICluster) => cluster.clusterType !== 'shared_qt',
  );

  if (isWorkflowsLoading || isResourcesLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  function noRunnigResource(protocolKey: string) {
    return _find(filteredResources, { type: protocolKey });
  }

  return (
    <Layout>
      <Layout.MainContainer fullWidth>
        <Grid gap={6} responsiveConfig={threeColumnGrid}>
          {workflows &&
            _keys(workflows)
              .filter(noRunnigResource)
              .map((protocolKey) => (
                <ProtocolStatusCard
                  key={protocolKey}
                  protocolKey={protocolKey}
                  resources={filteredResources}
                />
              ))}
        </Grid>
      </Layout.MainContainer>
    </Layout>
  );
}
